import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCompaniesStore } from 'src/store/companies/companies.store';
import { useCouponsStore } from 'src/store/benefits/coupons.store';
import { useProductsStore } from 'src/store/products/products.store';
import { useThemeStore } from 'src/store/products/theme.store';
import { useLpEventsStore } from 'src/store/benefits/lp-events.store';
import { useSnackbar } from 'notistack';

type BenefitTypeValue = 'product' | 'coupon';
type TierValue = 'all' | 'silver' | 'gold' | 'diamond';
export type BenefitStatusFilterValue = 'active' | 'inactive' | 'expired' | 'soldOut';
type LocalTypeValue = 'company' | 'local';
type ExhibitionEnvValue = 'viva' | 'lp_events' | 'whatsapp' | 'reward' | 'parking';

type Option<T extends string> = {
  label: string;
  value: T;
};

export type BenefitsDrawerFilterDraft = {
  benefitType: BenefitTypeValue[];
  isNewBenefit: boolean | null;
  tierSort: TierValue[];
  startDate: Date | null;
  endDate: Date | null;
  status: BenefitStatusFilterValue | '';
  localType: LocalTypeValue;
  company: string;
  exhibitionEnv: ExhibitionEnvValue[];
  categoriesId: string[];
  themesId: string[];
  businessUnitId: string;
  lpEventId: string;
};

type BenefitsFilterField =
  | 'benefitType'
  | 'visibleForTiers'
  | 'startDate'
  | 'endDate'
  | 'status'
  | 'statuses'
  | 'company'
  | 'exhibitionEnv'
  | 'categoriesId'
  | 'themesId'
  | 'businessUnitId'
  | 'lpEventId';

type BenefitsFilterValue = string | string[] | boolean;

export type BenefitsMappedFilterItem = {
  field?: BenefitsFilterField;
  column?: 'isNewBenefit';
  value: BenefitsFilterValue;
};

export type BenefitsMappedFilters = BenefitsMappedFilterItem[];

export type BenefitsDrawerApplyPayload = {
  mappedFilters: BenefitsMappedFilters;
  draftFilters: BenefitsDrawerFilterDraft;
};

type DateFieldsFormValues = {
  startDate: Date | null;
  endDate: Date | null;
};

type UseBenefitsDrawerFiltersParams = {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (payload: BenefitsDrawerApplyPayload) => void;
  appliedFilters: BenefitsDrawerFilterDraft | null;
};

const BENEFIT_TYPE_OPTIONS: Option<BenefitTypeValue>[] = [
  { label: 'Cupom', value: 'coupon' },
  { label: 'Produto', value: 'product' },
];

const TIER_OPTIONS: Option<TierValue>[] = [
  { label: 'Todos', value: 'all' },
  { label: 'Prata', value: 'silver' },
  { label: 'Ouro', value: 'gold' },
  { label: 'Diamante', value: 'diamond' },
];

const EXHIBITION_ENV_OPTIONS: Option<ExhibitionEnvValue>[] = [
  { label: 'VIVA', value: 'viva' },
  { label: 'Viva Visitante', value: 'lp_events' },
  { label: 'Whatsapp', value: 'whatsapp' },
  { label: 'TRILHA', value: 'reward' },
  // Estacionamento oculto nas opções de filtro.
  // { label: 'Estacionamento', value: 'parking' },
];

const STATUS_OPTIONS: Option<BenefitStatusFilterValue>[] = [
  { label: 'Ativo', value: 'active' },
  { label: 'Inativo', value: 'inactive' },
  { label: 'Expirado', value: 'expired' },
  { label: 'Esgotado', value: 'soldOut' },
];

const LOCAL_TYPE_OPTIONS: Option<LocalTypeValue>[] = [
  { label: 'Empresa', value: 'company' },
  { label: 'Local', value: 'local' },
];

const getBackendFallbackDateRange = () => ({
  startDate: new Date(2025, 0, 20),
  endDate: new Date(2099, 11, 31),
});

const createEmptyDraft = (): BenefitsDrawerFilterDraft => ({
  benefitType: [],
  isNewBenefit: null,
  tierSort: [],
  startDate: null,
  endDate: null,
  status: '',
  localType: 'company',
  company: '',
  exhibitionEnv: [],
  categoriesId: [],
  themesId: [],
  businessUnitId: '',
  lpEventId: '',
});

const cloneDraft = (draft: BenefitsDrawerFilterDraft): BenefitsDrawerFilterDraft => ({
  benefitType: [...draft.benefitType],
  isNewBenefit: typeof draft.isNewBenefit === 'boolean' ? draft.isNewBenefit : null,
  tierSort: [...draft.tierSort],
  startDate: draft.startDate ? new Date(draft.startDate) : null,
  endDate: draft.endDate ? new Date(draft.endDate) : null,
  status: draft.status,
  localType: draft.localType,
  company: draft.company,
  exhibitionEnv: [...(draft.exhibitionEnv || [])],
  categoriesId: [...(draft.categoriesId || [])],
  themesId: [...(draft.themesId || [])],
  businessUnitId: draft.businessUnitId || '',
  lpEventId: draft.lpEventId || '',
});

const normalizeDateInput = (value: Date | string | null): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const brazilianDateMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (brazilianDateMatch) {
    const [, day, month, year] = brazilianDateMatch;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    const isValidDate =
      parsed.getFullYear() === Number(year) &&
      parsed.getMonth() === Number(month) - 1 &&
      parsed.getDate() === Number(day);

    return isValidDate ? parsed : null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeOptionalDateRange = (
  startDateInput: Date | string | null,
  endDateInput: Date | string | null
): { startDate: Date | null; endDate: Date | null } => {
  const startDate = normalizeDateInput(startDateInput);
  const endDate = normalizeDateInput(endDateInput);

  if (!startDate && !endDate) {
    return {
      startDate: null,
      endDate: null,
    };
  }

  const safeStartDate = startDate || endDate;
  const safeEndDate = endDate || startDate;

  if (!safeStartDate || !safeEndDate) {
    return {
      startDate: null,
      endDate: null,
    };
  }

  if (safeEndDate < safeStartDate) {
    return {
      startDate: safeStartDate,
      endDate: new Date(safeStartDate),
    };
  }

  return {
    startDate: safeStartDate,
    endDate: safeEndDate,
  };
};

const withSafeDateRange = (draft: BenefitsDrawerFilterDraft): BenefitsDrawerFilterDraft => {
  const { startDate, endDate } = normalizeOptionalDateRange(draft.startDate, draft.endDate);

  return {
    ...draft,
    startDate,
    endDate,
  };
};

export const restoreBenefitsDrawerFilterDraft = (
  draft: BenefitsDrawerFilterDraft | null | undefined
): BenefitsDrawerFilterDraft | null => {
  if (!draft) {
    return null;
  }

  return withSafeDateRange(cloneDraft(draft));
};

const asArray = (value: BenefitsFilterValue): string[] => {
  if (Array.isArray(value)) {
    return value;
  }

  return typeof value === 'string' ? [value] : [];
};

export const restoreBenefitsDrawerFilterDraftFromMappedFilters = (
  filters: BenefitsMappedFilters | null | undefined
): BenefitsDrawerFilterDraft | null => {
  if (!filters?.length) {
    return null;
  }

  const restored = createEmptyDraft();

  filters.forEach((filter) => {
    if (filter.column === 'isNewBenefit') {
      restored.isNewBenefit = typeof filter.value === 'boolean' ? filter.value : null;
      return;
    }

    const values = asArray(filter.value);

    switch (filter.field) {
      case 'benefitType':
        restored.benefitType = values.filter(
          (value): value is BenefitTypeValue => value === 'coupon' || value === 'product'
        );
        break;
      case 'visibleForTiers':
        restored.tierSort = normalizeTierSelection(
          values.filter((value): value is TierValue =>
            ['all', 'silver', 'gold', 'diamond'].includes(value)
          )
        );
        break;
      case 'startDate':
        restored.startDate = normalizeDateInput(values[0] || null);
        break;
      case 'endDate':
        restored.endDate = normalizeDateInput(values[0] || null);
        break;
      case 'status': {
        const status = values[0];
        restored.status = ['active', 'inactive', 'expired', 'soldOut'].includes(status)
          ? (status as BenefitStatusFilterValue)
          : '';
        break;
      }
      case 'company':
        restored.company = values[0] || '';
        break;
      case 'exhibitionEnv':
        restored.exhibitionEnv = values.filter((value): value is ExhibitionEnvValue =>
          ['viva', 'lp_events', 'whatsapp', 'reward', 'parking'].includes(value)
        );
        break;
      case 'categoriesId':
        restored.categoriesId = values;
        break;
      case 'themesId':
        restored.themesId = values;
        break;
      case 'businessUnitId':
        restored.businessUnitId = values[0] || '';
        break;
      case 'lpEventId':
        restored.lpEventId = values[0] || '';
        break;
      default:
        break;
    }
  });

  return withSafeDateRange(restored);
};

const resolveDateRangeForApi = (
  startDateInput: Date | string | null,
  endDateInput: Date | string | null
): { startDate: Date; endDate: Date } => {
  const { startDate, endDate } = normalizeOptionalDateRange(startDateInput, endDateInput);

  if (!startDate || !endDate) {
    return getBackendFallbackDateRange();
  }

  return {
    startDate,
    endDate,
  };
};

const normalizeTierSelection = (values: TierValue[]): TierValue[] => {
  const unique = Array.from(new Set(values));
  const hasAll = unique.includes('all');
  const specificTiers = unique.filter((tier) => tier !== 'all') as TierValue[];

  if (specificTiers.length >= 3) {
    return ['all'];
  }

  if (hasAll) {
    return ['all'];
  }

  return specificTiers;
};

const formatDateForApi = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

export const mapBenefitsDrawerFilters = (
  draft: BenefitsDrawerFilterDraft
): BenefitsMappedFilters => {
  const mapped: BenefitsMappedFilters = [];
  const dateRange = resolveDateRangeForApi(draft.startDate, draft.endDate);

  const uniqueBenefitTypes = Array.from(new Set(draft.benefitType));
  const validBenefitTypes = uniqueBenefitTypes.filter(
    (value): value is BenefitTypeValue => value === 'coupon' || value === 'product'
  );

  if (validBenefitTypes.length === 1) {
    mapped.push({ field: 'benefitType', value: validBenefitTypes[0] });
  } else if (validBenefitTypes.length > 1) {
    mapped.push({ field: 'benefitType', value: validBenefitTypes });
  }

  if (typeof draft.isNewBenefit === 'boolean') {
    mapped.push({ column: 'isNewBenefit', value: draft.isNewBenefit });
  }

  const uniqueTiers = Array.from(new Set(draft.tierSort));
  if (uniqueTiers.length === 1) {
    mapped.push({ field: 'visibleForTiers', value: uniqueTiers[0] });
  } else if (uniqueTiers.length > 1) {
    mapped.push({ field: 'visibleForTiers', value: uniqueTiers });
  }

  const uniqueExhibitionEnvs = Array.from(new Set(draft.exhibitionEnv));
  if (uniqueExhibitionEnvs.length === 1) {
    mapped.push({ field: 'exhibitionEnv', value: uniqueExhibitionEnvs[0] });
  } else if (uniqueExhibitionEnvs.length > 1) {
    mapped.push({ field: 'exhibitionEnv', value: uniqueExhibitionEnvs });
  }

  mapped.push({ field: 'startDate', value: formatDateForApi(dateRange.startDate) });
  mapped.push({ field: 'endDate', value: formatDateForApi(dateRange.endDate) });

  if (draft.status) {
    mapped.push({ field: 'status', value: draft.status });
  }

  const normalizedCompany = draft.company.trim();
  if (normalizedCompany) {
    mapped.push({ field: 'company', value: normalizedCompany });
  }

  const mongoIdPattern = /^[a-f\d]{24}$/i;
  if (mongoIdPattern.test(draft.businessUnitId || '')) {
    mapped.push({ field: 'businessUnitId', value: draft.businessUnitId });
  }
  if (mongoIdPattern.test(draft.lpEventId || '')) {
    mapped.push({ field: 'lpEventId', value: draft.lpEventId });
  }
  const categoriesId = Array.from(new Set(draft.categoriesId)).filter((id) =>
    mongoIdPattern.test(id)
  );
  const themesId = Array.from(new Set(draft.themesId)).filter((id) => mongoIdPattern.test(id));

  if (categoriesId.length) {
    mapped.push({ field: 'categoriesId', value: categoriesId });
  }

  if (themesId.length) {
    mapped.push({ field: 'themesId', value: themesId });
  }

  return mapped;
};

export default function useBenefitsDrawerFilters({
  isOpen,
  onClose,
  onApplyFilters,
  appliedFilters,
}: UseBenefitsDrawerFiltersParams) {
  const { enqueueSnackbar } = useSnackbar();
  const [getAllBusinessUnits, getLpEventsList, businessUnits, lpEventsList, loadingBusinessUnits, loadingLpEvents] = useLpEventsStore((state) => [
    state.getAllBusinessUnits,
    state.getLpEventsList,
    state.businessUnits,
    state.lpEventsList,
    state.isLoadingBusinessUnits,
    state.isLoadingLpEventsList,
  ]);
  const [fetchCompanies, companies, loadingCompanies] = useCompaniesStore((state) => [
    state.fetchCompanies,
    state.companies,
    state.loadingPage,
  ]);
  const [getLocations, locations, loadingLocations] = useCouponsStore((state) => [
    state.getLocations,
    state.locations,
    state.isLoadingLocations,
  ]);
  const [getCategoriesList, categoriesList, loadingCategories] = useProductsStore((state) => [
    state.getCategoriesList,
    state.categoriesList,
    state.loadingCategoriesList,
  ]);
  const [getThemesList, themesList, loadingThemes] = useThemeStore((state) => [
    state.getThemesList,
    state.themesList,
    state.loadingThemesList,
  ]);

  const [draftFilters, setDraftFilters] = useState<BenefitsDrawerFilterDraft>(
    () => restoreBenefitsDrawerFilterDraft(appliedFilters) || createEmptyDraft()
  );
  const hasLoadedThemeAndCategoryOptions = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    Promise.all([getAllBusinessUnits(), getLpEventsList()]).catch((error) => {
      enqueueSnackbar(error.message || 'Não foi possível carregar as unidades e eventos do Viva Visitante.', { variant: 'error' });
    });
  }, [isOpen, getAllBusinessUnits, getLpEventsList, enqueueSnackbar]);

  const lpEventOptions = useMemo(() => lpEventsList
    .filter((event) => !draftFilters.businessUnitId || String(event.businessUnitId?._id || event.businessUnitId) === draftFilters.businessUnitId)
    .map((event) => ({ label: event.name, value: String(event._id) })), [lpEventsList, draftFilters.businessUnitId]);

  const handleChangeBusinessUnit = useCallback((value: string) => {
    setDraftFilters((current) => ({ ...current, businessUnitId: value, lpEventId: '' }));
  }, []);

  const handleChangeLpEvent = useCallback((value: string) => {
    setDraftFilters((current) => ({ ...current, lpEventId: value }));
  }, []);

  const { control, reset, setValue } = useForm<DateFieldsFormValues>({
    defaultValues: {
      startDate: null,
      endDate: null,
    },
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    fetchCompanies();
    getLocations();

    if (!hasLoadedThemeAndCategoryOptions.current) {
      hasLoadedThemeAndCategoryOptions.current = true;
      getCategoriesList();
      getThemesList();
    }
    const cloned = restoreBenefitsDrawerFilterDraft(appliedFilters) || createEmptyDraft();
    setDraftFilters(cloned);
    reset({
      startDate: cloned.startDate,
      endDate: cloned.endDate,
    });
  }, [
    appliedFilters,
    fetchCompanies,
    getCategoriesList,
    getLocations,
    getThemesList,
    isOpen,
    reset,
  ]);

  const companyOptions = useMemo(
    () => companies.map((company) => ({ label: company.name, value: company.name })),
    [companies]
  );

  const locationOptions = useMemo(
    () =>
      locations
        .filter((location) => location.value !== 'all')
        .map((location) => ({ label: location.label, value: location.label })),
    [locations]
  );

  const categoryOptions = useMemo(
    () =>
      categoriesList
        .map((category) => ({ label: category.name, value: category._id || '' }))
        .filter((option) => option.value && option.label),
    [categoriesList]
  );

  const themeOptions = useMemo(
    () =>
      themesList
        .map((theme) => ({ label: theme.name, value: theme._id || '' }))
        .filter((option) => option.value && option.label),
    [themesList]
  );

  const handleChangeBenefitType = useCallback((values: BenefitTypeValue[]) => {
    setDraftFilters((current) => ({ ...current, benefitType: values }));
  }, []);

  const handleChangeIsNewBenefit = useCallback((value: boolean | null) => {
    setDraftFilters((current) => ({ ...current, isNewBenefit: value }));
  }, []);

  const handleChangeTierSort = useCallback((values: TierValue[]) => {
    setDraftFilters((current) => ({ ...current, tierSort: normalizeTierSelection(values) }));
  }, []);

  const handleChangeExhibitionEnv = useCallback((values: ExhibitionEnvValue[]) => {
    setDraftFilters((current) => ({ ...current, exhibitionEnv: values }));
  }, []);

  const handleChangeCategories = useCallback((values: string[]) => {
    setDraftFilters((current) => ({ ...current, categoriesId: values }));
  }, []);

  const handleChangeThemes = useCallback((values: string[]) => {
    setDraftFilters((current) => ({ ...current, themesId: values }));
  }, []);

  const handleChangeStatus = useCallback((value: BenefitStatusFilterValue | '') => {
    setDraftFilters((current) => ({ ...current, status: value }));
  }, []);

  const handleChangeLocalType = useCallback((value: LocalTypeValue) => {
    setDraftFilters((current) => ({
      ...current,
      localType: value,
      company: '',
    }));
  }, []);

  const handleChangeDate = useCallback(
    (field: 'startDate' | 'endDate', value: Date | null) => {
      if (!value) {
        const clearedDraft = {
          ...draftFilters,
          startDate: null,
          endDate: null,
        };

        setDraftFilters(clearedDraft);
        setValue('startDate', null, { shouldDirty: true });
        setValue('endDate', null, { shouldDirty: true });
        return;
      }

      const nextDraft = { ...draftFilters, [field]: value };
      const normalizedDraft = withSafeDateRange(nextDraft);

      setDraftFilters(normalizedDraft);
      setValue('startDate', normalizedDraft.startDate, { shouldDirty: true });
      setValue('endDate', normalizedDraft.endDate, { shouldDirty: true });
    },
    [draftFilters, setValue]
  );

  const handleChangeCompany = useCallback((value: string) => {
    setDraftFilters((current) => ({ ...current, company: value }));
  }, []);

  const handleApply = useCallback(() => {
    const normalizedDraft = withSafeDateRange(cloneDraft(draftFilters));
    const mapped = mapBenefitsDrawerFilters(normalizedDraft);
    setDraftFilters(normalizedDraft);
    onApplyFilters({ mappedFilters: mapped, draftFilters: normalizedDraft });
    onClose();
  }, [draftFilters, onApplyFilters, onClose]);

  const handleClear = useCallback(() => {
    const empty = createEmptyDraft();
    setDraftFilters(empty);
    reset({ startDate: empty.startDate, endDate: empty.endDate });
    onApplyFilters({ mappedFilters: mapBenefitsDrawerFilters(empty), draftFilters: empty });
  }, [onApplyFilters, reset]);

  return {
    control,
    businessUnitOptions: businessUnits as Option<string>[],
    lpEventOptions,
    loadingBusinessUnits,
    loadingLpEvents,
    handleChangeBusinessUnit,
    handleChangeLpEvent,
    draftFilters,
    loadingCompanies,
    loadingLocations,
    loadingCategories,
    loadingThemes,
    companyOptions,
    locationOptions,
    categoryOptions,
    themeOptions,
    benefitTypeOptions: BENEFIT_TYPE_OPTIONS,
    tierOptions: TIER_OPTIONS,
    statusOptions: STATUS_OPTIONS,
    localTypeOptions: LOCAL_TYPE_OPTIONS,
    exhibitionEnvOptions: EXHIBITION_ENV_OPTIONS,
    handleChangeExhibitionEnv,
    handleChangeCategories,
    handleChangeThemes,
    handleChangeBenefitType,
    handleChangeIsNewBenefit,
    handleChangeTierSort,
    handleChangeStatus,
    handleChangeLocalType,
    handleChangeDate,
    handleChangeCompany,
    handleApply,
    handleClear,
  };
}
